import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    AfterViewInit,
    inject,
} from '@angular/core';
import { CdkDrag, CdkDragStart, CdkDragEnd, CdkDropList, CdkDragDrop } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { FormsModule } from '@angular/forms';
import { Organization } from '@newgenus/common';
import { ProfileColorService } from '../services/profile-color.service';
import { OrgDialogService } from '../services/org-dialog.service';

export interface PoolUser {
    uid: string;
    name: string;
    email?: string;
    avatar?: string;
    photoThumbnail?: string;
    /** Flag to indicate this user came from the pool (not from a position) */
    fromPool?: boolean;
}

@Component({
    selector: 'shared-user-pool-drawer',
    imports: [CommonModule, MaterialModule, FormsModule, CdkDrag, CdkDropList],
    template: `
        <!-- Toggle Button. On desktop it hides while the side drawer is open; on
         mobile it persists so it toggles the bottom strip open/closed. -->
        <button
            *ngIf="!isOpen || isMobile"
            mat-fab
            [color]="isOpen ? 'accent' : 'primary'"
            class="drawer-toggle-button"
            (click)="toggleDrawer()"
            [matTooltip]="isOpen ? 'Close Staff Pool' : 'Open Staff Pool'"
            matTooltipPosition="left"
        >
            <mat-icon>people</mat-icon>
        </button>

        <!-- Drawer Panel -->
        <div class="user-pool-drawer" [class.open]="isOpen">
            <!-- Drawer Header -->
            <div class="drawer-header">
                <div class="header-content">
                    <mat-icon>people</mat-icon>
                    <span class="title">Staff Pool</span>
                    <span class="user-count">({{ filteredUsers.length }})</span>
                </div>
                <button mat-icon-button (click)="toggleDrawer()" matTooltip="Close">
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <!-- Search Field -->
            <div class="search-container">
                <mat-form-field appearance="outline" class="w-100">
                    <mat-label>Search staff</mat-label>
                    <input
                        matInput
                        [(ngModel)]="searchTerm"
                        (ngModelChange)="filterUsers()"
                        placeholder="Type to search..."
                    />
                    <mat-icon matSuffix *ngIf="!searchTerm">search</mat-icon>
                    <button *ngIf="searchTerm" mat-icon-button matSuffix (click)="clearSearch()" matTooltip="Clear">
                        <mat-icon>clear</mat-icon>
                    </button>
                </mat-form-field>
            </div>

            <!-- User List with cdkDropList for drag-drop integration -->
            <div
                class="user-list"
                cdkDropList
                #userPoolList="cdkDropList"
                [cdkDropListData]="filteredUsers"
                [cdkDropListConnectedTo]="connectedDropLists"
                [cdkDropListSortingDisabled]="true"
                (cdkDropListDropped)="onUserDroppedBackToPool($event)"
            >
                <div *ngIf="filteredUsers.length === 0" class="no-users">
                    <mat-icon>person_off</mat-icon>
                    <p *ngIf="searchTerm">No staff match "{{ searchTerm }}"</p>
                    <p *ngIf="!searchTerm">No staff available</p>
                </div>

                <div
                    *ngFor="let user of filteredUsers; trackBy: trackByUid"
                    class="user-item"
                    cdkDrag
                    [cdkDragData]="createDragData(user)"
                    (cdkDragStarted)="onDragStarted($event, user)"
                    (cdkDragEnded)="onDragEnded($event, user)"
                >
                    <!-- Drag Preview -->
                    <div *cdkDragPreview class="user-drag-preview">
                        <img [src]="getAvatarUrl(user)" class="avatar-preview" />
                        <span>{{ user.name | titlecase }}</span>
                    </div>

                    <!-- Drag Placeholder - keep item visible in pool -->
                    <div *cdkDragPlaceholder class="user-drag-placeholder-hidden"></div>

                    <!-- User Card -->
                    <div class="user-card">
                        <span class="user-avatar-wrap">
                            <img [src]="getAvatarUrl(user)" class="user-avatar" />
                            <span *ngIf="isUserOnline(user)" class="pool-online-dot"></span>
                        </span>
                        <div class="user-info">
                            <span class="user-name">{{ user.name | titlecase }}</span>
                            <span class="user-email" *ngIf="user.email">{{ user.email }}</span>
                        </div>
                        <mat-icon class="drag-handle" matTooltip="Drag to org part">drag_indicator</mat-icon>
                    </div>
                </div>
            </div>

            <!-- Footer / Help Text -->
            <div class="drawer-footer">
                <mat-icon>info</mat-icon>
                <span>Drag staff onto org parts to assign hats</span>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                position: absolute;
                right: 0;
                top: 0;
                height: 100%;
                z-index: 100;
                pointer-events: none;
            }

            .drawer-toggle-button {
                position: absolute;
                right: 16px;
                /* This toggle only shows in board EDIT mode, which hides the app header —
         the one thing padding the status-bar inset — so on a phone it sat half
         under the Android clock (Paul, 2026-08-04). env() is 0 in a browser. */
                top: calc(10px + var(--ng-top-inset, env(safe-area-inset-top, 0px)));
                pointer-events: auto;
                z-index: 101;
                /* Shrink from default mat-fab 56px to 40px so the toggle fits inside
         the org-board header strip (56px) without poking out. */
                width: 40px;
                height: 40px;

                ::ng-deep .mdc-fab__ripple,
                ::ng-deep .mat-mdc-button-touch-target {
                    width: 40px;
                    height: 40px;
                }

                ::ng-deep .mat-mdc-focus-indicator {
                    width: 40px;
                    height: 40px;
                }

                ::ng-deep .mat-icon {
                    font-size: 22px;
                    width: 22px;
                    height: 22px;
                    line-height: 22px;
                }
            }

            .user-pool-drawer {
                position: absolute;
                right: 0;
                top: 0;
                height: 100%;
                width: 320px;
                background: #2d2d2d;
                box-shadow: -4px 0 12px rgba(0, 0, 0, 0.4);
                transform: translateX(100%);
                transition: transform 0.3s ease-in-out;
                display: flex;
                flex-direction: column;
                pointer-events: auto;
            }

            .user-pool-drawer.open {
                transform: translateX(0);
            }

            .drawer-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                background: #1e1e1e;
                color: white;
                border-bottom: 1px solid #444;
            }

            .header-content {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .header-content .title {
                font-size: 16px;
                font-weight: 500;
            }

            .header-content .user-count {
                font-size: 12px;
                opacity: 0.7;
            }

            .drawer-header button {
                color: white;
            }

            .search-container {
                padding: 12px 16px 0;
                background: #2d2d2d;
            }

            .search-container .mat-mdc-form-field {
                width: 100%;
            }

            /* Dark theme form field overrides */
            .search-container ::ng-deep .mat-mdc-text-field-wrapper {
                background-color: #3d3d3d !important;
            }

            .search-container ::ng-deep .mat-mdc-form-field-flex {
                background-color: #3d3d3d;
            }

            .search-container ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__leading,
            .search-container ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__notch,
            .search-container ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__trailing {
                border-color: #555 !important;
            }

            .search-container ::ng-deep .mat-mdc-form-field-label,
            .search-container ::ng-deep .mdc-floating-label {
                color: #aaa !important;
            }

            .search-container ::ng-deep .mat-mdc-input-element {
                color: white !important;
                caret-color: white;
            }

            .search-container ::ng-deep .mat-mdc-form-field-icon-suffix {
                color: #aaa;
            }

            .user-list {
                flex: 1;
                overflow-y: auto;
                padding: 8px 16px;
                background: #2d2d2d;
            }

            .no-users {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 32px;
                color: #888;
                text-align: center;
            }

            .no-users mat-icon {
                font-size: 48px;
                width: 48px;
                height: 48px;
                margin-bottom: 8px;
                opacity: 0.5;
            }

            .user-item {
                margin-bottom: 8px;
                cursor: grab;
            }

            .user-item:active {
                cursor: grabbing;
            }

            .user-card {
                display: flex;
                align-items: center;
                padding: 8px 12px;
                background: #3d3d3d;
                border-radius: 8px;
                border: 1px solid #4d4d4d;
                transition: all 0.2s ease;
            }

            .user-card:hover {
                background: #4d4d4d;
                border-color: #ffc107;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            }

            .user-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                border: 2px solid #555;
            }

            .user-avatar-wrap {
                position: relative;
                display: inline-flex;
                flex-shrink: 0;
                margin-right: 12px;
            }

            .pool-online-dot {
                position: absolute;
                top: -2px;
                right: -2px;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: #2e7d32;
                border: 2px solid white;
                z-index: 1;
                pointer-events: none;
            }

            .user-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                min-width: 0;
            }

            .user-name {
                font-weight: 500;
                font-size: 14px;
                color: #fff;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .user-email {
                font-size: 12px;
                color: #aaa;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .pending-badge-small {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                font-size: 10px;
                color: #ff9800;
                font-weight: 500;
            }

            .has-pending {
                border-left: 3px solid #ff9800;
            }

            .drag-handle {
                color: #777;
                cursor: grab;
            }

            .user-drag-preview {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: #ffc107;
                color: #1e1e1e;
                border-radius: 24px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                font-weight: 500;
            }

            .avatar-preview {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: 2px solid #1e1e1e;
            }

            .user-drag-placeholder {
                height: 56px;
                background: #3d3d3d;
                border: 2px dashed #ffc107;
                border-radius: 8px;
                margin-bottom: 8px;
            }

            .drawer-footer {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 12px 16px;
                background: #1e1e1e;
                border-top: 1px solid #444;
                font-size: 12px;
                color: #888;
            }

            .drawer-footer mat-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
            }

            /* Responsive */
            @media (max-width: 600px) {
                .user-pool-drawer {
                    width: 100%;
                }
            }

            /* ═══ Light theme overrides ═══ */
            :host-context(:not(.dark-theme)) {
                .user-pool-drawer {
                    background: #f5f5f5;
                    box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
                }

                .drawer-header {
                    background: #e0e0e0;
                    color: rgba(0, 0, 0, 0.87);
                    border-color: rgba(0, 0, 0, 0.12);
                }

                .drawer-header button {
                    color: rgba(0, 0, 0, 0.65);
                }
                .header-content .user-count {
                    opacity: 0.6;
                }

                .search-container {
                    background: #f5f5f5;
                }

                .search-container ::ng-deep .mat-mdc-text-field-wrapper {
                    background-color: #fff !important;
                }

                .search-container ::ng-deep .mat-mdc-form-field-flex {
                    background-color: #fff;
                }

                .search-container ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__leading,
                .search-container ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__notch,
                .search-container ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__trailing {
                    border-color: rgba(0, 0, 0, 0.23) !important;
                }

                .search-container ::ng-deep .mat-mdc-form-field-label,
                .search-container ::ng-deep .mdc-floating-label {
                    color: rgba(0, 0, 0, 0.6) !important;
                }

                .search-container ::ng-deep .mat-mdc-input-element {
                    color: rgba(0, 0, 0, 0.87) !important;
                    caret-color: var(--entity-color-users, #1976d2);
                }

                .search-container ::ng-deep .mat-mdc-form-field-icon-suffix {
                    color: rgba(0, 0, 0, 0.54);
                }

                .user-list {
                    background: #f5f5f5;
                }
                .no-users {
                    color: rgba(0, 0, 0, 0.45);
                }

                .user-card {
                    background: #fff;
                    border-color: rgba(0, 0, 0, 0.12);
                }

                .user-card:hover {
                    background: #fafafa;
                    border-color: #ffc107;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
                }

                .user-name {
                    color: rgba(0, 0, 0, 0.87);
                }
                .user-email {
                    color: rgba(0, 0, 0, 0.54);
                }
                .drag-handle {
                    color: rgba(0, 0, 0, 0.35);
                }

                .drawer-footer {
                    background: #e0e0e0;
                    border-color: rgba(0, 0, 0, 0.12);
                    color: rgba(0, 0, 0, 0.54);
                }
            }

            /* ═══ Mobile: button-activated bottom-docked pool ═══
       The toggle button (top-right) opens a compact strip that slides UP from the
       bottom of the board — the board stays visible above it so staff can be
       dragged UP onto a post (same cdkDrag wiring). Close via the header button.
       Search + ~3 users; scroll for more. */
            @media (max-width: 767.98px) {
                /* Fixed to the viewport, sitting ABOVE the mobile bottom-tab nav (≈56px +
         safe-area) so the nav doesn't cover the user list. Anchors to the screen
         regardless of how tall/scrolled the board container is. */
                .user-pool-drawer {
                    position: fixed;
                    top: auto;
                    bottom: calc(56px + env(safe-area-inset-bottom));
                    left: 0;
                    right: 0;
                    width: 100%;
                    height: auto;
                    max-height: 55vh;
                    z-index: 1100;
                    transform: translateY(
                        calc(100% + 56px + env(safe-area-inset-bottom))
                    ); /* hidden below the nav until opened */
                    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.35);
                    border-top-left-radius: 12px;
                    border-top-right-radius: 12px;
                }

                .user-pool-drawer.open {
                    transform: translateY(0);
                }

                /* Very thin header — shrink its close button so it doesn't force height. */
                .drawer-header {
                    padding: 2px 4px 2px 12px;
                }
                .drawer-header .title {
                    font-size: 13px;
                }
                .drawer-header .header-content mat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
                .drawer-header button.mat-mdc-icon-button {
                    width: 32px;
                    height: 32px;
                    padding: 0;
                }
                .drawer-header button.mat-mdc-icon-button ::ng-deep .mat-mdc-button-touch-target {
                    width: 32px;
                    height: 32px;
                }

                .drawer-footer {
                    display: none; /* keep it compact; header retains the close button */
                }

                /* Tighten the gap between search and the list: small padding + drop the
         form-field's subscript (hint/error) space entirely. */
                .search-container {
                    padding: 4px 12px 0;
                }
                .search-container ::ng-deep .mat-mdc-form-field-subscript-wrapper {
                    display: none;
                }

                /* ~2 user cards visible; scroll for the rest. */
                .user-list {
                    max-height: 120px;
                    padding: 2px 12px 8px;
                }

                .user-item {
                    margin-bottom: 6px;
                }

                .user-card {
                    padding: 6px 10px;
                }
            }
        `,
    ],
})
export class UserPoolDrawerComponent implements OnInit, OnChanges, AfterViewInit {
    private profileColorService = inject(ProfileColorService);
    private dialogService = inject(OrgDialogService);

    @Input() organization: Organization | null = null;
    @Input() isEditing = false;
    /** Connected drop lists from the org board positions */
    @Input() connectedDropLists: CdkDropList[] = [];

    @Output() userDragStarted = new EventEmitter<{ user: PoolUser; event: CdkDragStart }>();
    @Output() userDragEnded = new EventEmitter<{ user: PoolUser; event: CdkDragEnd }>();
    @Output() drawerToggled = new EventEmitter<boolean>();
    @Output() userDroppedOnPosition = new EventEmitter<{ user: PoolUser; event: CdkDragDrop<PoolUser[]> }>();
    @Output() userRemovedFromPosition = new EventEmitter<{ user: PoolUser; event: CdkDragDrop<PoolUser[]> }>();
    /** Emits the user pool drop list reference once available, so the parent can add it to its drop lists */
    @Output() poolDropListReady = new EventEmitter<CdkDropList>();

    @ViewChild('userPoolList') userPoolDropList!: CdkDropList;

    isOpen = false;
    searchTerm = '';
    allUsers: PoolUser[] = [];
    filteredUsers: PoolUser[] = [];

    /** Phone-sized viewport — the toggle button persists so it can close the strip. */
    get isMobile(): boolean {
        return typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;
    }

    ngOnInit(): void {
        this.loadUsers();
    }

    ngAfterViewInit(): void {
        // Emit the drop list reference once it's available
        // Use setTimeout to ensure the ViewChild is fully initialized
        setTimeout(() => {
            if (this.userPoolDropList) {
                // console.log('User pool drop list available, emitting reference');
                this.poolDropListReady.emit(this.userPoolDropList);
            } else {
                console.warn('User pool drop list NOT available in ngAfterViewInit');
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organization']) {
            this.loadUsers();
        }
        // Auto-close drawer when exiting edit mode
        if (changes['isEditing'] && !this.isEditing) {
            this.isOpen = false;
        }
        // Debug: Log when connectedDropLists changes
        // if (changes['connectedDropLists']) {
        //   const prev = changes['connectedDropLists'].previousValue?.length || 0;
        //   const curr = changes['connectedDropLists'].currentValue?.length || 0;
        //   console.log(`User pool connectedDropLists changed: ${prev} -> ${curr}`);
        //   if (curr > 0) {
        //     const dropListIds = changes['connectedDropLists'].currentValue.map((dl: CdkDropList) => dl.id);
        //     console.log('ALL Connected drop list IDs:', dropListIds);
        //   }
        // }
    }

    private loadUsers(): void {
        if (!this.organization?.orgUsers) {
            this.allUsers = [];
            this.filteredUsers = [];
            return;
        }

        const userKeys = this.organization.orgUsers.userKeys || [];
        this.allUsers = userKeys
            .map((uid) => {
                const orgUser = (this.organization as Organization).orgUsers[uid];
                return {
                    uid,
                    name: orgUser?.name || orgUser?.userName || 'Unknown User',
                    email: '', // Email not available on OrgUser interface
                    photoThumbnail: orgUser?.photoThumbnail,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name));

        // Ensure profile colors are loaded for all users
        this.profileColorService.ensureLoadedMany(userKeys);

        this.filterUsers();
    }

    filterUsers(): void {
        if (!this.searchTerm.trim()) {
            this.filteredUsers = [...this.allUsers];
            return;
        }

        const term = this.searchTerm.toLowerCase().trim();
        this.filteredUsers = this.allUsers.filter(
            (user) => user.name.toLowerCase().includes(term) || (user.email && user.email.toLowerCase().includes(term))
        );
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.filterUsers();
    }

    isUserOnline(user: PoolUser): boolean {
        return !!user?.uid && this.dialogService.onlineUserIds?.includes(user.uid);
    }

    toggleDrawer(): void {
        this.isOpen = !this.isOpen;
        this.drawerToggled.emit(this.isOpen);
    }

    getAvatarUrl(user: PoolUser): string {
        // Prefer photoThumbnail if available
        if (user.photoThumbnail) {
            return user.photoThumbnail;
        }
        return this.profileColorService.getAvatarUrl(user.uid, user.name || 'User', 40);
    }

    trackByUid(index: number, user: PoolUser): string {
        return user.uid;
    }

    /**
     * Check if user has any pending hat assignments in the organization.
     */
    isUserPending(user: PoolUser): boolean {
        if (!this.organization?.orgHats?.hatsKeys) return false;
        for (const hatKey of this.organization.orgHats.hatsKeys) {
            const hat = this.organization.orgHats[hatKey] as any;
            if (!hat || hat.isSoftDeleted) continue;
            if (hat.pendingAssignedToArray?.includes(user.uid)) return true;
        }
        return false;
    }

    // Track the last known drag position for fallback drop detection
    private lastDragPosition: { x: number; y: number } | null = null;
    private currentDragUser: PoolUser | null = null;

    onDragStarted(event: CdkDragStart, user: PoolUser): void {
        // Debug: Log connected drop lists at drag start
        console.log(`User pool drag start - connectedDropLists count: ${this.connectedDropLists?.length || 0}`);
        if (this.userPoolDropList) {
            console.log(`User pool own ID: ${this.userPoolDropList.id}`);
            // Check what the CDK thinks the connected lists are
            const cdkConnectedTo = this.userPoolDropList.connectedTo;
            console.log(`CDK connectedTo type: ${typeof cdkConnectedTo}, isArray: ${Array.isArray(cdkConnectedTo)}`);
            if (Array.isArray(cdkConnectedTo)) {
                const ids = cdkConnectedTo.slice(0, 5).map((dl: any) => (typeof dl === 'string' ? dl : dl.id));
                console.log(`CDK connectedTo IDs (first 5): ${ids.join(', ')}`);
            }
        }

        // Track the current drag user and position for fallback detection
        this.currentDragUser = user;
        this.lastDragPosition = null;

        // Listen for pointer move to track position
        const doc = document;
        const moveHandler = (e: PointerEvent) => {
            this.lastDragPosition = { x: e.clientX, y: e.clientY };
        };
        const upHandler = () => {
            doc.removeEventListener('pointermove', moveHandler);
            doc.removeEventListener('pointerup', upHandler);
        };
        doc.addEventListener('pointermove', moveHandler);
        doc.addEventListener('pointerup', upHandler);

        this.userDragStarted.emit({ user, event });
    }

    onDragEnded(event: CdkDragEnd, user: PoolUser): void {
        console.log('User pool drag ended - drop position:', event.source.getFreeDragPosition());
        console.log(
            'User pool drag ended - was dropped in container:',
            event.source.dropContainer?.id || 'NO CONTAINER (drop failed)'
        );
        console.log('User pool drag ended - last tracked position:', this.lastDragPosition);

        // Additional debug: Check if drop was outside all containers
        const dropContainer = event.source.dropContainer;
        const containerClasses = dropContainer?.element?.nativeElement?.className || '';
        if (dropContainer) {
            console.log('Drop container element:', containerClasses);
        }

        // If drop ended back in the pool (CDK detection failed), emit with last known position
        // The parent can use this position to manually detect the drop target
        // BUT only if the container does NOT have 'cdk-drop-list-receiving' class
        // (if it has receiving, CDK will also fire onDrop and we'd get duplicates)
        const isDropInPool = dropContainer?.id === this.userPoolDropList?.id;
        const cdkWillAlsoFire = containerClasses.includes('cdk-drop-list-receiving');

        if (isDropInPool && this.lastDragPosition && !cdkWillAlsoFire) {
            console.log('CDK drop detection failed (no receiving) - emitting fallback position');
            // Emit a special event with the position for fallback handling
            this.userDragEnded.emit({
                user,
                event,
                fallbackPosition: this.lastDragPosition,
            } as any);
        } else {
            if (cdkWillAlsoFire) {
                console.log('CDK will handle this drop (has receiving class) - skipping fallback');
            }
            this.userDragEnded.emit({ user, event });
        }

        this.currentDragUser = null;
        this.lastDragPosition = null;
    }

    /**
     * Creates drag data with a flag indicating the user came from the pool.
     * This helps distinguish pool drags from position-to-position drags.
     */
    createDragData(user: PoolUser): PoolUser {
        return { ...user, fromPool: true };
    }

    /**
     * Called when a user is dropped back into the pool from a position.
     * This is the REMOVE operation.
     */
    onUserDroppedBackToPool(event: CdkDragDrop<PoolUser[]>): void {
        // Only handle if dropped from a different container (i.e., from a position)
        if (event.previousContainer !== event.container) {
            const user = event.item.data as PoolUser;
            console.log('User dropped back to pool (REMOVE):', user);
            this.userRemovedFromPosition.emit({ user, event });
        }
        // If dropped within the same container, do nothing (pool doesn't reorder)
    }
}
